var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));

// components/theme/common/src/inject-fonts-for-theme.ts
import { getEmotionInstance, injectFont } from "@gs-ux-uitoolkit-common/style";
import { TwoKeyWeakSet } from "gs-uitk-set-utils";
var fontsInjectedSet = new TwoKeyWeakSet();
function injectFontsForTheme(theme, { emotionInstance = getEmotionInstance() } = {}) {
  if (theme == null ? void 0 : theme.fonts) {
    const fontsAlreadyInjected = fontsInjectedSet.has(theme, emotionInstance);
    if (!fontsAlreadyInjected) {
      theme.fonts.forEach((fontFace) => injectFont(fontFace, { emotionInstance }));
      fontsInjectedSet.add(theme, emotionInstance);
    }
  }
}

// components/theme/common/src/themes/light-theme.ts
import {
  colorNames,
  colors as colors3,
  elevationVariants,
  typographyVariants,
  getFontFaces
} from "@gs-ux-uitoolkit-common/design-system";
import { rgba as rgba2 } from "polished";

// components/theme/common/src/utils/color.ts
import {
  colors
} from "@gs-ux-uitoolkit-common/design-system";
var colorLookup = {};
Object.keys(colors).forEach((value) => {
  colorLookup[colors[value]] = value;
});
function getColorValueFromHex(hex) {
  return colorLookup[hex];
}
var parseColorValue = (value) => {
  const parts = `${value}`.match(/([a-z]+)([0-9]+)/);
  if (parts) {
    const name = parts[1];
    const swatch = parts[2];
    return { name, swatch };
  }
  return null;
};
var lighten = (value, steps, useWhite) => {
  const parsedColor = parseColorValue(value);
  if (!parsedColor) {
    return value;
  }
  let swatchNum = Number(parsedColor.swatch) - steps * 10;
  if (swatchNum < 10) {
    if (useWhite) {
      return "white";
    }
    swatchNum = 10;
  }
  return `${parsedColor.name}${swatchNum < 100 ? "0" : ""}${swatchNum}`;
};
var darken = (value, steps, useBlack) => {
  const parsedColor = parseColorValue(value);
  if (!parsedColor) {
    return value;
  }
  const maxSwatchNum = value.startsWith("gray") ? 110 : 100;
  let swatchNum = Number(parsedColor.swatch) + steps * 10;
  if (swatchNum > maxSwatchNum) {
    if (useBlack) {
      return "black";
    }
    swatchNum = maxSwatchNum;
  }
  return `${parsedColor.name}${swatchNum < 100 ? "0" : ""}${swatchNum}`;
};
function changeContrast(color3, amount, fn) {
  const colorValue = getColorValueFromHex(color3.toString());
  if (!colorValue) {
    throw new Error(
      [
        `Invalid color "${color3}" provided to "changeContrast(...)". `,
        `Only valid GS Design System colors are allowed in this theme`
      ].join(" ")
    );
  }
  return colors[fn(colorValue, amount, true)];
}

// components/theme/common/src/utils/interaction.ts
import {
  colors as colors2
} from "@gs-ux-uitoolkit-common/design-system";
import { meetsContrastGuidelines, mix, rgba } from "polished";
function getColorShades(theme, mode, value, emphasis = "bold") {
  let color3;
  const colorEmphasis = emphasis === "minimal" ? "bold" : emphasis;
  if (value in theme.color) {
    color3 = theme.color[value][colorEmphasis];
  } else if (value in theme.status) {
    color3 = theme.status[value][colorEmphasis];
  } else if (value in theme.colorScheme) {
    color3 = theme.colorScheme[value];
  } else {
    throw new Error(
      `Invalid value "${value}" provided to getRampInteractionShades. Allowed types are ColorName | Status | keyof ColorVariantProps`
    );
  }
  return getShades(theme, mode, color3, emphasis);
}
function getSurfaceShades(theme, mode, surface3, emphasis) {
  if (emphasis === "bold") {
    return getShades(theme, mode, theme.colorScheme.primary, "bold");
  }
  if (emphasis === "minimal" || emphasis === "subtle") {
    const color3 = theme.surface[surface3].toString();
    const themeColor = theme.colorScheme.primary.toString();
    const background = surface3 === "none" ? rgba(themeColor, 0.2) : mix(0.2, themeColor, color3);
    const hover = surface3 === "none" ? rgba(themeColor, 0.4) : mix(0.2, themeColor, background);
    const active = surface3 === "none" ? rgba(themeColor, 0.6) : mix(0.4, themeColor, background);
    const text4 = theme.text[surfaceToTypographyMap[surface3]];
    const border4 = theme.border[surfaceToBorderMap[surface3]];
    return {
      background,
      hover,
      active,
      text: text4,
      border: border4
    };
  }
  const surfaceOverlayContrast = mode === "dark" ? 4 : 3;
  const shades = surface3 === "none" ? {
    background: theme.surface.none,
    active: rgba(mode === "light" ? colors2.gray030 : colors2.gray070, 0.4),
    hover: rgba(mode === "light" ? colors2.gray030 : colors2.gray070, 0.2)
  } : getShades(theme, mode, theme.surface[surface3], "bold", surfaceOverlayContrast);
  const text3 = theme.text[surfaceToTypographyMap[surface3]];
  const border3 = theme.border[surfaceToBorderMap[surface3]];
  return __spreadProps(__spreadValues({}, shades), {
    text: text3,
    border: border3
  });
}
function getInvalidColorMessage(color3, fn) {
  return [
    `Invalid color "${color3}" provided to "${fn}(...)". `,
    `Only valid GS Design System colors are allowed in this theme`
  ].join(" ");
}
function getShades(theme, mode, color3, emphasis = "bold", overlayContrast = 2) {
  let colorValue = getColorValueFromHex(color3.toString());
  if (!colorValue) {
    throw new Error(getInvalidColorMessage(color3.toString(), "getInteractionShades"));
  }
  if (colorValue === "white" || colorValue === "transparent") {
    colorValue = "gray010";
  }
  if (colorValue === "black") {
    colorValue = "gray110";
  }
  const parsedColor = parseColorValue(colorValue);
  if (!parsedColor) {
    throw new Error(`"${color3}" could not be parsed to a valid GS Design System color`);
  }
  const swatch = Number(parsedColor.swatch);
  const variationFnMap = {
    bold: () => {
      const background = color3.toString();
      let overlay = theme.increaseContrast(colors2[colorValue], overlayContrast).toString();
      const overlayAmount = 0.2;
      let text3 = theme.text.reversed;
      if (mode === "light" && swatch > 90) {
        overlay = colors2.gray060;
      }
      if (mode === "dark" && swatch < 20) {
        overlay = colors2.gray030;
      }
      const hover = mix(overlayAmount, overlay, background);
      const active = mix(overlayAmount * 2, overlay, background);
      const meetsContrast = meetsContrastGuidelines(background, text3.toString());
      if (!meetsContrast.AA && mode === "light") {
        text3 = colors2.gray110;
      }
      if (!meetsContrast.AA && mode === "dark") {
        text3 = colors2.white;
      }
      const border3 = "transparent";
      return {
        background,
        hover,
        active,
        text: text3,
        border: border3
      };
    },
    subtle: () => {
      const background = color3.toString();
      const overlay = theme.increaseContrast(color3, 2).toString();
      const hover = mix(0.2, overlay, background);
      const active = mix(0.4, overlay, background);
      let text3 = theme.increaseContrast(background, 5);
      const meetsContrast = meetsContrastGuidelines(background, text3.toString());
      if (mode === "light" && (!meetsContrast.AA || color3 === theme.color.yellow.subtle)) {
        text3 = colors2.gray090;
      }
      if (mode === "dark" && (!meetsContrast.AA || color3 === theme.color.yellow.subtle)) {
        text3 = colors2.gray110;
      }
      const border3 = text3;
      return {
        background,
        hover,
        active,
        text: text3,
        border: border3
      };
    },
    minimal: () => {
      const background = "transparent";
      const hover = rgba(color3.toString(), 0.1);
      const active = rgba(color3.toString(), 0.2);
      let text3 = color3.toString();
      while (!meetsContrastGuidelines(theme.surface.primary.toString(), text3.toString()).AA) {
        text3 = theme.increaseContrast(text3, 1).toString();
      }
      const border3 = "transparent";
      return {
        background,
        hover,
        active,
        text: text3,
        border: border3
      };
    }
  };
  return variationFnMap[emphasis]();
}
var surfaceToTypographyMap = {
  primary: "primary",
  secondary: "primary",
  tertiary: "primary",
  moderate: "primary",
  bold: "primary",
  strong: "reversed",
  contrast: "reversed",
  none: "primary"
};
var surfaceToBorderMap = {
  primary: "minimal",
  secondary: "minimal",
  tertiary: "subtle",
  moderate: "moderate",
  bold: "bold",
  strong: "bold",
  contrast: "contrast",
  none: "minimal"
};

// components/theme/common/src/themes/light-theme.ts
var color = colorNames.reduce(
  (obj, colorName) => {
    obj[colorName] = {
      bold: colors3[`${colorName}060`],
      subtle: colors3[`${colorName}020`]
    };
    return obj;
  },
  {}
);
color.yellow.bold = colors3.yellow030;
color.yellow.subtle = colors3.yellow010;
var status = {
  none: color.gray,
  information: color.blue,
  success: color.green,
  warning: color.yellow,
  warningAlt: {
    bold: colors3.orange050,
    subtle: colors3.orange020
  },
  error: color.red,
  loading: color.blue
};
var text = {
  primary: colors3.gray090,
  secondary: colors3.gray070,
  tertiary: colors3.gray040,
  link: colors3.blue060,
  destructive: colors3.red060,
  reversed: colors3.white
};
var surface = {
  primary: colors3.white,
  secondary: colors3.gray010,
  tertiary: colors3.gray020,
  moderate: colors3.gray030,
  bold: colors3.gray040,
  strong: colors3.gray060,
  contrast: colors3.gray110,
  none: rgba2(colors3.white, 0)
};
var colorScheme = {
  primary: colors3.blue060,
  secondary: colors3.gray020,
  info: colors3.blue100,
  header: colors3.blue090,
  overlay: rgba2(colors3.gray090, 0.8)
};
var border = {
  minimal: colors3.gray020,
  subtle: colors3.gray030,
  moderate: colors3.gray040,
  strong: colors3.gray050,
  bold: colors3.gray060,
  contrast: colors3.gray090,
  reversed: colors3.white,
  input: colors3.gray050
};
var state = {
  disabledOpacity: 0.3
};
var elevation = Object.keys(elevationVariants).map((elevation3) => ({
  [elevation3]: {
    shadow: elevationVariants[elevation3].boxShadow,
    background: surface.primary
  }
})).reduce(
  (target, cur) => __spreadValues(__spreadValues({}, target), cur),
  {}
);
var dataviz = {
  sequence01: colors3.blue070,
  sequence02: colors3.orange050,
  sequence03: colors3.teal050,
  sequence04: colors3.yellow040,
  sequence05: colors3.purple050,
  sequence06: colors3.ultramarine090,
  sequence07: colors3.lime050,
  sequence08: colors3.pink060,
  sequence09: colors3.aqua040,
  sequence10: colors3.green060
};
var typography = Object.entries(typographyVariants).reduce(
  (obj, [name, value]) => {
    obj[name] = value;
    return obj;
  },
  {}
);
var lightTheme = {
  colorMode: "light",
  color,
  status,
  colorScheme,
  text,
  surface,
  border,
  state,
  dataviz,
  elevation,
  typography,
  fonts: getFontFaces(),
  getColorInteractionShades: (...args) => getColorShades(lightTheme, "light", ...args),
  getSurfaceInteractionShades: (...args) => getSurfaceShades(lightTheme, "light", ...args),
  increaseContrast: (...args) => changeContrast(...args, darken),
  decreaseContrast: (...args) => changeContrast(...args, lighten)
};

// components/theme/common/src/create-theme.ts
import { merge } from "gs-uitk-lodash";
function createTheme(base, mode, theme) {
  const newTheme = merge({}, lightTheme, base, theme);
  newTheme.getColorInteractionShades = (...args) => getColorShades(newTheme, mode, ...args);
  newTheme.getSurfaceInteractionShades = (...args) => getSurfaceShades(newTheme, mode, ...args);
  return newTheme;
}

// components/theme/common/src/themes/dark-theme.ts
import {
  colorNames as colorNames2,
  colors as colors4,
  elevationVariants as elevationVariants2,
  typographyVariants as typographyVariants2,
  getFontFaces as getFontFaces2
} from "@gs-ux-uitoolkit-common/design-system";
import { mix as mix2, rgba as rgba3 } from "polished";
var color2 = colorNames2.reduce(
  (obj, colorName) => {
    obj[colorName] = {
      bold: colors4[`${colorName}050`],
      subtle: colors4[`${colorName}080`]
    };
    return obj;
  },
  {}
);
color2.yellow.bold = colors4.yellow030;
color2.yellow.subtle = colors4.yellow050;
color2.orange.bold = colors4.orange040;
var colorScheme2 = {
  primary: colors4.blue050,
  secondary: colors4.gray080,
  info: colors4.white,
  header: colors4.gray090,
  overlay: rgba3(colors4.gray110, 0.8)
};
var status2 = {
  none: color2.gray,
  information: color2.blue,
  success: color2.green,
  warning: color2.yellow,
  warningAlt: color2.yellow,
  error: color2.red,
  loading: color2.blue
};
var text2 = {
  primary: colors4.gray010,
  secondary: colors4.gray040,
  tertiary: colors4.gray060,
  link: colors4.blue050,
  destructive: colors4.red050,
  reversed: colors4.gray110
};
var surface2 = {
  primary: colors4.gray110,
  secondary: colors4.gray100,
  tertiary: colors4.gray090,
  moderate: colors4.gray080,
  bold: colors4.gray060,
  strong: colors4.gray040,
  contrast: colors4.white,
  none: rgba3(colors4.gray110, 0)
};
var border2 = {
  minimal: colors4.gray080,
  subtle: colors4.gray070,
  moderate: colors4.gray060,
  strong: colors4.gray050,
  bold: colors4.gray040,
  contrast: colors4.gray020,
  reversed: colors4.gray110,
  input: colors4.gray060
};
var state2 = {
  disabledOpacity: 0.3
};
var elevation2 = Object.keys(elevationVariants2).reduce(
  (obj, elevation3, i) => {
    obj[elevation3] = {
      shadow: elevationVariants2[elevation3].boxShadow,
      background: i === 0 ? surface2.primary : mix2(0.2 + i * 0.04, colors4.gray070, surface2.primary.toString()).toString()
    };
    return obj;
  },
  {}
);
var dataviz2 = {
  sequence01: colors4.blue050,
  sequence02: colors4.orange050,
  sequence03: colors4.teal050,
  sequence04: colors4.yellow030,
  sequence05: colors4.purple050,
  sequence06: colors4.ultramarine030,
  sequence07: colors4.lime050,
  sequence08: colors4.pink040,
  sequence09: colors4.aqua040,
  sequence10: colors4.green040
};
var typography2 = Object.entries(typographyVariants2).reduce(
  (obj, [name, value]) => {
    obj[name] = value;
    return obj;
  },
  {}
);
var darkTheme = {
  colorMode: "dark",
  color: color2,
  status: status2,
  colorScheme: colorScheme2,
  text: text2,
  surface: surface2,
  border: border2,
  state: state2,
  dataviz: dataviz2,
  elevation: elevation2,
  typography: typography2,
  fonts: getFontFaces2(),
  getColorInteractionShades: (...args) => getColorShades(darkTheme, "dark", ...args),
  getSurfaceInteractionShades: (...args) => getSurfaceShades(darkTheme, "dark", ...args),
  increaseContrast: (...args) => changeContrast(...args, lighten),
  decreaseContrast: (...args) => changeContrast(...args, darken)
};

// components/theme/common/src/css-var-utils.ts
import {
  emphases
} from "@gs-ux-uitoolkit-common/design-system";
var isUpperCase = (character) => character === character.toUpperCase();
var isHyphenChar = (character) => character === "-";
var isNum = (character) => !isNaN(Number(character));
function formatTokenName(token) {
  return token.split("").map(
    (char) => (
      // converts uppercase letter of camelCase to lowercase excluding the hyphens as we already might have a hyphen character in between. uppercase of number is the number itself so it might introduce unnnecessary hyphens
      isUpperCase(char) && !isHyphenChar(char) && !isNum(char) ? `-${char.toLowerCase()}` : char
    )
  ).join("");
}
function getCoreTokens(theme) {
  const tokens = [];
  const keys = Object.keys(theme);
  const coreTokenKeys = ["color"];
  const colorTokenKeys = ["surface", "text", "border", "dataviz", "status"];
  keys.forEach((key) => {
    if (key !== "fonts") {
      const tokenValue = theme[key];
      const tokenKey = coreTokenKeys.indexOf(key) > -1 ? `${key}-core` : colorTokenKeys.indexOf(key) > -1 ? `color-${key}` : key;
      if (typeof tokenValue === "object") {
        const childTokens = getCoreTokens(tokenValue);
        const nestedTokens = childTokens.map((childToken) => __spreadProps(__spreadValues({}, childToken), {
          token: formatTokenName(`${tokenKey}-${childToken.token}`)
        }));
        tokens.push(...nestedTokens);
      } else if (typeof tokenValue === "string" || typeof tokenValue === "number") {
        tokens.push({
          token: formatTokenName(tokenKey),
          value: tokenValue
        });
      }
    }
  });
  return tokens;
}
function getEmphasisTokens({
  coreTokenObject,
  tokenKey,
  prefix,
  theme
}) {
  const tokens = [];
  const replacementEmphasis = {
    minimal: "bold"
  };
  const interactions = ["hover", "active", "text"];
  emphases.forEach((emphasis) => {
    tokens.push({
      token: formatTokenName(`${prefix}-${tokenKey}-${emphasis}`),
      value: coreTokenObject[replacementEmphasis[emphasis] || emphasis].toString()
    });
    const interactionShades = theme.getColorInteractionShades(
      tokenKey,
      emphasis
    );
    interactions.forEach((interaction) => {
      tokens.push({
        token: formatTokenName(`${prefix}-${tokenKey}-${emphasis}-${interaction}`),
        value: interactionShades[interaction].toString()
      });
    });
  });
  return tokens;
}
function getColorDerivedTokens(theme) {
  const { color: color3 } = theme;
  const colors5 = Object.keys(color3);
  let tokens = [];
  const themeObject = theme.color;
  colors5.forEach((color4) => {
    tokens = [
      ...tokens,
      ...getEmphasisTokens({
        coreTokenObject: themeObject[color4],
        tokenKey: color4,
        prefix: "color-core",
        theme
      })
    ];
  });
  return tokens;
}
function getStatusDerivedTokens(theme) {
  const { status: status3 } = theme;
  const statuses = Object.keys(status3);
  let tokens = [];
  const themeObject = theme.status;
  statuses.forEach((status4) => {
    tokens = [
      ...tokens,
      ...getEmphasisTokens({
        coreTokenObject: themeObject[status4],
        tokenKey: status4,
        prefix: "color-status",
        theme
      })
    ];
  });
  return tokens;
}
function getSurfaceInteractionTokens(surface3, theme) {
  const tokens = [];
  const interactions = ["hover", "active", "text", "border"];
  const surfaceInteractionShades = theme.getSurfaceInteractionShades(surface3);
  interactions.forEach((interaction) => {
    tokens.push({
      token: formatTokenName(`color-surface-${surface3}-${interaction}`),
      value: surfaceInteractionShades[interaction].toString()
    });
  });
  return tokens;
}
function getSurfaceEmphasisInteractionTokens(surface3, theme) {
  const tokens = [];
  const interactions = ["background", "hover", "active", "text", "border"];
  emphases.forEach((emphasis) => {
    const surfaceInteractionShades = theme.getSurfaceInteractionShades(surface3, emphasis);
    if (emphasis !== "minimal") {
      interactions.forEach((interaction) => {
        if (interaction === "background") {
          tokens.push({
            token: formatTokenName(`color-surface-${surface3}-selected-${emphasis}`),
            value: surfaceInteractionShades[interaction].toString()
          });
          return;
        }
        tokens.push({
          token: formatTokenName(
            `color-surface-${surface3}-selected-${emphasis}-${interaction}`
          ),
          value: surfaceInteractionShades[interaction].toString()
        });
      });
    }
  });
  return tokens;
}
function getSurfaceDerivedTokens(theme) {
  const { surface: surface3 } = theme;
  const surfaces = Object.keys(surface3);
  let tokens = [];
  surfaces.forEach((surface4) => {
    tokens = [
      ...tokens,
      ...getSurfaceInteractionTokens(surface4, theme),
      ...getSurfaceEmphasisInteractionTokens(surface4, theme)
    ];
  });
  return tokens;
}
function getBorderDerivedTokens(theme) {
  const tokens = [];
  const { border: border3 } = theme;
  const borders = Object.keys(border3);
  borders.forEach((border4) => {
    tokens.push({
      token: formatTokenName(`color-border-${border4}-hover`),
      value: theme.increaseContrast(theme.border[border4], 1).toString()
    });
  });
  return tokens;
}
function getDerivedTokens(theme) {
  return [
    ...getColorDerivedTokens(theme),
    ...getStatusDerivedTokens(theme),
    ...getSurfaceDerivedTokens(theme),
    ...getBorderDerivedTokens(theme)
  ];
}
function generateCssVars(theme) {
  const coreTokens = getCoreTokens(theme);
  const derivedTokens = getDerivedTokens(theme);
  return [...coreTokens, ...derivedTokens].reduce(
    (tokens, currentToken) => __spreadProps(__spreadValues({}, tokens), {
      [`--gs-uitk-${currentToken.token}`]: currentToken.value
    }),
    {}
  );
}
function injectCssVars(theme, element) {
  const cssVariables = generateCssVars(theme);
  Object.keys(cssVariables).forEach((cssVariable) => {
    element.style.setProperty(cssVariable, cssVariables[cssVariable]);
  });
}

// components/theme/common/src/create-component-class-definitions.ts
function createComponentClassDefinitions(props, createDefaultStyledClasses, styleOverrides) {
  if (!styleOverrides) {
    return createDefaultStyledClasses(props);
  } else {
    if (typeof styleOverrides === "function") {
      return styleOverrides({
        props,
        createDefaultStyledClasses: () => createDefaultStyledClasses(props)
      });
    } else {
      return styleOverrides;
    }
  }
}
export {
  createComponentClassDefinitions,
  createTheme,
  darkTheme,
  formatTokenName,
  generateCssVars,
  injectCssVars,
  injectFontsForTheme,
  lightTheme
};
//# sourceMappingURL=index.mjs.map
